/**
 * Single source of truth for every colour in the app.
 *
 * Consumed by three layers, all derived from this object:
 *  - antd tokens        -> src/components/DefaultTheme.tsx
 *  - CSS custom props   -> applyCssVars(), read by src/assets/css/styles.css
 *  - inline styles      -> imported directly where a component needs a value
 *
 * The only place a colour is duplicated is the pre-hydration <style> block in
 * public/index.html, which must paint before this module can run.
 */
export const palette = {
    bg: '#0B0B0F',
    surface: '#14141A',
    surfaceRaised: '#1B1B22',
    border: '#26262E',

    text: '#F5F5F7',
    textSecondary: '#C4C4CC',
    muted: '#8E8E99',
    faint: '#5C5C66',

    accent: '#E8B14C',
    accentHover: '#F3C468',
    accentPressed: '#D69C33',
    accentBg: '#261F16',
    accentBorder: '#4D3D21',
    /** Text/icons drawn on top of an accent fill. 10.1:1 against accent. */
    onAccent: '#0B0B0F',

    success: '#3DD68C',
    successBg: '#11231E',
    successBorder: '#1A4835',

    danger: '#FF5C5C',
    dangerBg: '#281518',
    dangerBorder: '#542326',

    mask: 'rgba(11, 11, 15, 0.78)',
} as const;

const cssVars: Record<string, string> = {
    '--rm-bg': palette.bg,
    '--rm-surface': palette.surface,
    '--rm-surface-raised': palette.surfaceRaised,
    '--rm-border': palette.border,
    '--rm-text': palette.text,
    '--rm-text-secondary': palette.textSecondary,
    '--rm-muted': palette.muted,
    '--rm-accent': palette.accent,
    '--rm-accent-hover': palette.accentHover,
    '--rm-accent-pressed': palette.accentPressed,
    '--rm-on-accent': palette.onAccent,
    '--rm-success': palette.success,
    '--rm-danger': palette.danger,
    '--rm-mask': palette.mask,
    '--rm-radius': '12px',
    '--rm-radius-lg': '16px',
    '--rm-tap': '48px',
    // Vivid dashboard: a gold glow and the accent gradient, both derived from the accent trio above.
    // The glow's rgb is `palette.accent` (#E8B14C) at 45% alpha.
    '--rm-glow': '0 0 18px rgba(232, 177, 76, 0.45)',
    '--rm-gradient-gold': `linear-gradient(135deg, ${palette.accentHover} 0%, ${palette.accent} 45%, ${palette.accentPressed} 100%)`,
};

/**
 * Writes the palette onto <html> as custom properties. Must run before the
 * first React render so styles.css never resolves a missing var.
 */
export const applyCssVars = (root: HTMLElement = document.documentElement) => {
    Object.entries(cssVars).forEach(([name, value]) => root.style.setProperty(name, value));
};
